<!--
  Visual hierarchy (AGENTS.md §Visual hierarchy)
  Question: "Is this household active, and can I disable it or bring it back?"
  Answer:   the household's status (StatusTag, never colour alone) and what that status means.
  Context:  the ledger's name, which is the one thing the status applies to.
  Controls: a disabled household offers "Restore" as its one --color-action control; an active
            one offers "Disable" as an ordinary bordered button that opens an inline confirmation,
            since disabling locks every member out and must never be a single click.
-->
<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useSession } from '@/app/session'
import { LedgerLifecyclePanel, useLedgerLifecycle } from '@/features/ledger-lifecycle'

const { t } = useI18n()
const session = useSession()
const lifecycle = useLedgerLifecycle()

function disableActive(): void {
  if (session.activeLedger.value) lifecycle.disable(session.activeLedger.value.id)
}

function restoreActive(): void {
  if (session.activeLedger.value) lifecycle.restore(session.activeLedger.value.id)
}
</script>

<template>
  <main class="mx-auto flex w-full max-w-[92.5rem] flex-col gap-8 p-[var(--space-page)]">
    <h1 class="text-[length:var(--text-step-6)] font-semibold text-[color:var(--color-text)]">
      {{ t('ledgerLifecycle.title') }}
    </h1>

    <p v-if="session.isLoading.value" role="status">{{ t('app.loading') }}</p>

    <p v-else-if="session.activeLedger.value === undefined" class="text-[color:var(--color-text-muted)]">
      {{ t('ledgerLifecycle.noLedger') }}
    </p>

    <LedgerLifecyclePanel
      v-else
      :ledger-name="session.activeLedger.value.name"
      :is-disabled="session.activeLedger.value.archivedAt !== undefined"
      :is-disabling="lifecycle.isDisabling.value"
      :disable-error="lifecycle.disableError.value"
      :is-restoring="lifecycle.isRestoring.value"
      :restore-error="lifecycle.restoreError.value"
      @disable="disableActive"
      @restore="restoreActive"
    />
  </main>
</template>
